import { Button, Stack, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { finalize } from 'rxjs';

import { ManageWasteFamily, ValorisationType, wasteFamiliesQuery, wasteFamiliesService } from '@store/settings/wasteFamilies';

import { checkIfErrors, FieldValidationType, getFieldError } from '@utils/yup.utils';

import NumberTextFieldComponent from '@components/inputs/numberTextField/NumberTextField.component';
import SelectComponent from '@components/inputs/select/Select.component';
import TextFieldComponent from '@components/inputs/textField/TextField.component';
import ModalComponent from '@components/modal/Modal.component';

interface ManageWasteFamilyModalProps {
  isCreation?: boolean;
}

const ManageWasteFamilyModal = (props: ManageWasteFamilyModalProps) => {
  const { isCreation } = props;

  const { wasteFamilyId } = useParams();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);

  const [wasteFamily, setWasteFamily] = useState<ManageWasteFamily>({});
  const [wasteCategory, setWasteCategory] = useState<ValorisationType>();
  const [wasteCategories] = useState<ValorisationType[]>([
    ValorisationType.MATERIAL,
    ValorisationType.ENERGY,
  ]);

  useEffect(() => {
    if (wasteFamilyId) {
      const fetchedWasteFamily = wasteFamiliesQuery.wasteFamilyById(+wasteFamilyId) || {};
      setWasteFamily(fetchedWasteFamily);
    }
  }, [wasteFamilyId]);

  useEffect(() => {
    if (wasteFamily.valorisationType === ValorisationType.ENERGY) {
      setWasteCategory(ValorisationType.ENERGY);
    } else if (wasteFamily.valorisationType === ValorisationType.MATERIAL) {
      setWasteCategory(ValorisationType.MATERIAL);
    }
  }, [wasteFamily]);

  if (!isCreation && !wasteFamily.id) return null;

  const updateWasteFamily = (name: string) => (value: any) => setWasteFamily((state) => ({ ...state, [name]: value }));
  const updateWasteCategory = () => (value: any) => setWasteCategory(value);

  const handleClose = () => navigate('..', { replace: true });

  const handleSave = () => {
    if (isCreation) {
      setLoading(true);
      wasteFamiliesService.createWasteFamily(wasteFamily).pipe(finalize(() => setLoading(false)))
        .subscribe({
          next: () => {
            enqueueSnackbar(t('settings.waste.success.add.family'), { variant: 'success' });
            handleClose();
          },
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
    } else {
      if (!wasteFamilyId) return;
      setLoading(true);
      wasteFamily.valorisationType = wasteCategory;
      wasteFamiliesService.updateWasteFamily(wasteFamilyId, wasteFamily).pipe(finalize(() => setLoading(false)))
        .subscribe({
          next: () => {
            enqueueSnackbar(t('settings.waste.success.edit.family'), { variant: 'success' });
          },
          error: (err) => enqueueSnackbar(err.text, err.options),
        });
    }
  };

  const errors = {
    name: getFieldError(wasteFamily.name, FieldValidationType.REQUIRED_STRING),
    marketPriceInCents: getFieldError(wasteFamily.marketPriceInCents, FieldValidationType.REQUIRED_NUMBER),
  };

  return (
    <ModalComponent
      title={t(`settings.waste.manage.title.${isCreation ? 'add' : 'edit'}`)}
      handleClose={handleClose}
      content={(
        <Stack width="70%" spacing={2} pt="55px" pb="10px">
          <TextFieldComponent
            color="secondary"
            labelWidth={160}
            label={t('settings.waste.manage.name')}
            placeholder={t('global.writeSomething')}
            handleChange={updateWasteFamily('name')}
            value={wasteFamily.name || ''}
            error={errors.name}
            disabled={!isCreation}
            required
          />
          <TextFieldComponent
            color="secondary"
            labelWidth={160}
            label={t('global.translateKey')}
            placeholder={t('global.writeSomething')}
            handleChange={updateWasteFamily('nameTranslateKey')}
            value={wasteFamily.nameTranslateKey || ''}
          />
          <NumberTextFieldComponent
            color="secondary"
            labelWidth={160}
            label={t('settings.waste.manage.estimatedKilometersForProcessing')}
            placeholder={t('global.writeSomething')}
            pattern={new RegExp(/^(\d*|\d+)$/)}
            handleChange={updateWasteFamily('estimatedKilometersForProcessing')}
            value={wasteFamily.estimatedKilometersForProcessing ?? undefined}
            endAdornment={<Typography fontSize={12} fontWeight="400">
              KM
            </Typography>}
          />
          <TextFieldComponent
            color="secondary"
            labelWidth={160}
            label={t('settings.waste.manage.icon')}
            placeholder={t('global.writeSomething')}
            handleChange={updateWasteFamily('iconPath')}
            value={wasteFamily.iconPath ?? undefined} />
          <SelectComponent
            color="secondary"
            labelWidth={160}
            label={t('settings.waste.manage.wasteCategory')}
            placeholder={t('global.select')}
            handleChange={updateWasteCategory()}
            value={wasteCategory}
            items={wasteCategories.map((category) => ({ value: category, label: t(`settings.waste.manage.categories.${category}`) }))}
          />
        </Stack>
      )}
      actions={(
        <Button
          variant="contained"
          disabled={loading || checkIfErrors(errors)}
          onClick={handleSave}
          color="primary">
          {t('global.save')}
        </Button>
      )} />
  );
};

export default ManageWasteFamilyModal;